import React, { useCallback, useMemo, useState, useEffect, useContext, createContext } from 'react';

const LocaleContext = createContext({
  lang: 'en',
  setLang: () => {},
});

export const LocaleProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem('locale');
      if (saved === 'ru' || saved === 'en') return saved;
    } catch (e) {
      // ignore
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
    }
    return 'en';
  });

  // Keep the document language in sync with the selected UI locale
  useEffect(() => {
    try {
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = lang;
      }
    } catch (e) {
      // ignore
    }
  }, [lang]);

  useEffect(() => {
    try {
      localStorage.setItem('locale', lang);
    } catch (e) {
      // ignore
    }
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang }), [lang]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

const useLocale = () => {
  const ctx = useContext(LocaleContext);

  const getCurrentLanguage = useCallback(() => {
    if (ctx && (ctx.lang === 'ru' || ctx.lang === 'en')) {
      return ctx.lang;
    }
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
    }
    return 'en';
  }, [ctx]);

  const setLanguage = useCallback(
    (code) => {
      if (!ctx || !ctx.setLang) return;
      if (code === 'ru' || code === 'en') {
        ctx.setLang(code);
      }
    },
    [ctx],
  );

  const getLocalizedName = useCallback(
    (nameObj) => {
      const lang = getCurrentLanguage();

      if (!nameObj) return '';
      if (typeof nameObj !== 'object') return String(nameObj);

      const primaryKeys = [`name_${lang}`, lang];

      const fallbackKeys =
        lang === 'ru' ? ['name_en', 'en', 'name_ru', 'ru'] : ['name_ru', 'ru', 'name_en', 'en'];

      // тут я поправил баг с ".primaryKeys, .fallbackKeys"
      for (const key of [...primaryKeys, ...fallbackKeys]) {
        const value = nameObj[key];
        if (value) return value;
      }

      const anyVal = Object.values(nameObj).find((v) => v);
      return anyVal || '';
    },
    [getCurrentLanguage],
  );

  const getLocalizedFullName = useCallback(
    (full_name_object) => {
      if (full_name_object && typeof full_name_object === 'object') {
        const nameObj = full_name_object.name;
        const surnameObj = full_name_object.surname;

        const name = getLocalizedName(nameObj) || '';
        const surname = getLocalizedName(surnameObj) || '';

        const full = `${name} ${surname}`.trim();
        return full;
      }
      console.log('Not recognized full name object: ', full_name_object);
      return '';
    },
    [getLocalizedName],
  );

  const getLocalized = useCallback(
    (nameObj) => {
      const lang = getCurrentLanguage();
      if (nameObj && typeof nameObj === 'object') {
        return nameObj[lang] || nameObj.en || '';
      }
      return nameObj || '';
    },
    [getCurrentLanguage],
  );

  return {
    getCurrentLanguage,
    setLanguage, // <— ВАЖНО: отдаём наружу setter
    getLocalizedName,
    getLocalizedFullName,
    getLocalized,
  };
};

export default useLocale;
